import { date } from "@/lib/date";
import type { SaleWithRelations } from "@/types/sale-with-relations";
import type { Flavor, Ingredient, FlavorIngredient } from "generated/prisma/browser";
import { groupBy, sumBy, sortBy } from "lodash";

export type FlavorWithIngredients = Flavor & {
  ingredients?: (FlavorIngredient & { ingredient: Ingredient })[];
};

/**
 * Pedidos fecham 4,5 dias antes do fim do dia da entrega (`endDate`): ao meio-dia
 * de 3 dias antes. Entrega na sexta → pedidos até terça, 12h.
 */
export const ORDER_CLOSE_DAYS_BEFORE = 3;
export const ORDER_CLOSE_HOUR = 12;

export const closeDateOf = (endDate: string | Date) =>
  date(endDate as string)
    .startOf("date")
    .subtract(ORDER_CLOSE_DAYS_BEFORE, "days");

/** Instante exato do fechamento dos pedidos. */
export const closeAtOf = (endDate: string | Date) =>
  closeDateOf(endDate).hour(ORDER_CLOSE_HOUR);

type OrdersState = Pick<SaleWithRelations, "endDate" | "ordersClosedAt" | "ordersManual">;

/**
 * O prazo passou e o ADMIN não assumiu o controle das vendas: o primeiro acesso
 * depois disso grava `ordersClosedAt` (ver `GET /sales/active`).
 */
export const isPastDeadline = (sale: OrdersState) =>
  !sale.ordersManual && date().isAfter(closeAtOf(sale.endDate));

export const isOrdersOpen = (sale?: OrdersState | null) =>
  !!sale && !sale.ordersClosedAt && !isPastDeadline(sale);

/** "Pedidos até 28/10, 12h"; depois que o ADMIN assume, o estado que ele escolheu. */
export const deadlineLabel = (sale: OrdersState) =>
  sale.ordersManual
    ? isOrdersOpen(sale)
      ? "Vendas reabertas"
      : "Vendas fechadas"
    : `Pedidos até ${closeDateOf(sale.endDate).format("DD/MM")}, 12h`;

export const daysToClose = (sale: Pick<SaleWithRelations, "endDate">) =>
  Math.max(0, closeDateOf(sale.endDate).diff(date().startOf("date"), "day"));

/** 0–100: tempo decorrido entre o início da campanha e o fechamento dos pedidos. */
export const elapsedPercent = (
  sale: Pick<SaleWithRelations, "startDate" | "endDate">,
) => {
  const start = date(sale.startDate as unknown as string).startOf("date").valueOf();
  const end = closeAtOf(sale.endDate).valueOf();
  if (end <= start) return 100;
  const pct = ((date().valueOf() - start) / (end - start)) * 100;
  return Math.min(100, Math.max(0, Math.round(pct)));
};

type SaleOrder = SaleWithRelations["orders"][number];

export const pizzasOf = (orders: SaleOrder[]) =>
  sumBy(orders, (o) => sumBy(o.items, "quantity"));

/** Posição (1-based) de cada pedido na campanha, por ordem de criação. */
export const orderPositions = (orders: SaleOrder[]) => {
  const sorted = sortBy(orders, (o) => date(o.createdAt as unknown as string).valueOf());
  return new Map(sorted.map((o, i) => [o.id, i + 1]));
};

export type BarItem = { key: string; name: string; value: number; unit?: string; initials?: string };

export const byFlavor = (orders: SaleOrder[]): BarItem[] =>
  sortBy(
    Object.entries(
      groupBy(
        orders.flatMap((o) => o.items),
        (i) => i.flavor?.name ?? i.flavorId,
      ),
    ).map(([name, items]) => ({ key: name, name, value: sumBy(items, "quantity") })),
    (r) => -r.value,
  );

export const bySeller = (orders: SaleOrder[]): BarItem[] =>
  sortBy(
    Object.values(groupBy(orders, "userId")).map((group) => ({
      key: group[0]!.userId,
      name: group[0]!.user?.name ?? "—",
      value: pizzasOf(group),
    })),
    (r) => -r.value,
  );

/** Mesma conta da lista de compras em PDF: quantidade do ingrediente × pizzas. */
export const byIngredient = (
  orders: SaleOrder[],
  flavors: FlavorWithIngredients[],
): BarItem[] => {
  const flavorById = new Map(flavors.map((f) => [f.id, f]));
  const totals = new Map<string, BarItem>();

  for (const item of orders.flatMap((o) => o.items)) {
    for (const fi of flavorById.get(item.flavorId)?.ingredients ?? []) {
      const prev = totals.get(fi.ingredient.name);
      const value = (prev?.value ?? 0) + fi.quantity * item.quantity;
      totals.set(fi.ingredient.name, {
        key: fi.ingredient.name,
        name: fi.ingredient.name,
        unit: fi.ingredient.unit,
        value,
      });
    }
  }

  return sortBy([...totals.values()], (r) => -r.value);
};

/** "2 Calabresa, 1 Frango c/ Catupiry" */
export const orderSummary = (order: SaleOrder) =>
  order.items.map((i) => `${i.quantity} ${i.flavor?.name ?? ""}`.trim()).join(", ");

/**
 * Linha curta de ingredientes por sabor. Ingredientes presentes em todos os
 * sabores (massa, molho…) ficam de fora: não ajudam a distinguir um do outro.
 */
export const flavorDescriptions = (flavors: FlavorWithIngredients[]) => {
  const count = new Map<string, number>();
  for (const f of flavors)
    for (const fi of f.ingredients ?? [])
      count.set(fi.ingredient.name, (count.get(fi.ingredient.name) ?? 0) + 1);

  return new Map(
    flavors.map((f) => {
      const names = (f.ingredients ?? [])
        .map((fi) => fi.ingredient.name)
        .filter((n) => flavors.length < 2 || (count.get(n) ?? 0) < flavors.length);
      return [f.id, names.join(", ")] as const;
    }),
  );
};
